import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  BookOpen,
  Copy,
  Check,
  Search,
  Download,
  FileText,
  TrendingUp,
  Network,
  Clock,
  RefreshCw,
  ExternalLink,
  Sparkles,
  Plus,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import type { Citation, ResearchResource } from '@/types/research';
import { cn } from '@/lib/utils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const CITATION_FORMATS: { value: Citation['format']; label: string; description: string }[] = [
  { value: 'APA', label: 'APA 7th Edition', description: 'American Psychological Association' },
  { value: 'MLA', label: 'MLA 9th Edition', description: 'Modern Language Association' },
  { value: 'Chicago', label: 'Chicago 17th', description: 'Chicago Manual of Style' },
  { value: 'Harvard', label: 'Harvard', description: 'Harvard Referencing' },
  { value: 'IEEE', label: 'IEEE', description: 'Institute of Electrical and Electronics Engineers' },
  { value: 'Vancouver', label: 'Vancouver', description: 'Medical & Scientific Journals' },
];

// Sample citation history
const SAMPLE_CITATIONS: Citation[] = [
  {
    id: 'cit-001',
    resourceId: 'res-001',
    resourceTitle: 'Climate Change Impact on Great Barrier Reef Coral Bleaching Patterns 2020-2024',
    format: 'APA',
    formattedCitation: 'Mitchell, S., Chen, J., & Thompson, E. (2024). Climate Change Impact on Great Barrier Reef Coral Bleaching Patterns 2020-2024. https://doi.org/10.1234/reef.2024.001',
    authors: ['Dr. Sarah Mitchell', 'Prof. James Chen', 'Dr. Emma Thompson'],
    year: '2024',
    doi: '10.1234/reef.2024.001',
    createdAt: '2024-04-15T14:30:00Z',
  },
  {
    id: 'cit-002',
    resourceId: 'res-002',
    resourceTitle: 'Machine Learning Framework for Astronomical Object Classification',
    format: 'IEEE',
    formattedCitation: 'M. Wang and L. Anderson, "Machine Learning Framework for Astronomical Object Classification," 2023. doi: 10.5281/zenodo.7654321',
    authors: ['Dr. Michael Wang', 'Dr. Lisa Anderson'],
    year: '2023',
    doi: '10.5281/zenodo.7654321',
    createdAt: '2024-04-14T10:00:00Z',
  },
  {
    id: 'cit-003',
    resourceId: 'res-003',
    resourceTitle: 'Indigenous Land Management Practices and Bushfire Prevention: A 50-Year Analysis',
    format: 'Harvard',
    formattedCitation: "Williams, D., Nampijinpa, M. & Taylor, R. 2024, 'Indigenous Land Management Practices and Bushfire Prevention: A 50-Year Analysis', doi: 10.1038/nature.2024.5678",
    authors: ['Prof. David Williams', 'Dr. Mary Nampijinpa', 'Dr. Robert Taylor'],
    year: '2024',
    doi: '10.1038/nature.2024.5678',
    createdAt: '2024-04-12T16:45:00Z',
  },
];

// Sample citation trend data
const citationTrendData = [
  { month: 'Sep', citations: 12 },
  { month: 'Oct', citations: 18 },
  { month: 'Nov', citations: 15 },
  { month: 'Dec', citations: 22 },
  { month: 'Jan', citations: 28 },
  { month: 'Feb', citations: 35 },
  { month: 'Mar', citations: 42 },
  { month: 'Apr', citations: 47 },
];

export function ResearchCitationsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<Citation['format']>('APA');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generatedCitation, setGeneratedCitation] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: resources, isLoading: resourcesLoading } = useQuery({
    queryKey: ['research-resources-for-citation'],
    queryFn: () => researchService.listResources({ pageSize: 20 }),
  });

  const handleCopy = async (id: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleGenerateCitation = async (resourceId: string) => {
    setIsGenerating(true);
    try {
      const citation = await researchService.generateCitation(resourceId, selectedFormat);
      setGeneratedCitation(citation.formattedCitation);
    } catch (error) {
      console.error('Error generating citation:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Citations & References</h1>
          <p className="text-muted-foreground">
            Generate citations, track impact, and manage your research references
          </p>
        </div>
        <Button onClick={() => setShowGenerateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Generate Citation
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Citations</p>
                <p className="text-2xl font-bold">2,847</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                <BookOpen className="h-5 w-5 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold">+47</p>
                <p className="text-xs text-green-600 flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  +12% from last month
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Citing Works</p>
                <p className="text-2xl font-bold">1,234</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
                <Network className="h-5 w-5 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Generated Today</p>
                <p className="text-2xl font-bold">23</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
                <FileText className="h-5 w-5 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="generate" className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">Generate Citation</TabsTrigger>
          <TabsTrigger value="history">Recent Citations</TabsTrigger>
          <TabsTrigger value="impact">Impact Tracking</TabsTrigger>
        </TabsList>

        {/* Generate Tab */}
        <TabsContent value="generate" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Quick Citation Generator
              </CardTitle>
              <CardDescription>
                Search for a resource and generate a citation in your preferred format
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="md:col-span-2">
                  <Label>Search Resource</Label>
                  <div className="relative mt-1.5">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search by title, author, or DOI..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label>Citation Format</Label>
                  <Select value={selectedFormat} onValueChange={(v) => setSelectedFormat(v as Citation['format'])}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CITATION_FORMATS.map((format) => (
                        <SelectItem key={format.value} value={format.value}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{format.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Format Description */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>{CITATION_FORMATS.find((f) => f.value === selectedFormat)?.label}:</strong>{' '}
                  {CITATION_FORMATS.find((f) => f.value === selectedFormat)?.description}
                </p>
              </div>

              {/* Resource List */}
              {resourcesLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-20" />
                  ))}
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {resources?.data
                    .filter(
                      (r) =>
                        !searchQuery ||
                        r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        r.authors.some((a) => a.toLowerCase().includes(searchQuery.toLowerCase()))
                    )
                    .map((resource) => (
                      <Card key={resource.id} className="hover:border-primary/50 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate">{resource.title}</h4>
                              <p className="text-sm text-muted-foreground truncate">
                                {resource.authors.join(', ')} • {resource.publicationDate ? new Date(resource.publicationDate).getFullYear() : 'N/A'}
                              </p>
                              {resource.doi && (
                                <p className="text-xs text-primary mt-1">DOI: {resource.doi}</p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleGenerateCitation(resource.id)}
                              disabled={isGenerating}
                            >
                              {isGenerating ? (
                                <RefreshCw className="mr-2 h-3 w-3 animate-spin" />
                              ) : (
                                <BookOpen className="mr-2 h-3 w-3" />
                              )}
                              Cite
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              )}

              {/* Generated Citation Display */}
              {generatedCitation && (
                <Card className="border-primary/50 bg-primary/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Generated Citation ({selectedFormat})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 bg-background rounded-lg border">
                      <p className="text-sm font-mono whitespace-pre-wrap">{generatedCitation}</p>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy('generated', generatedCitation)}
                      >
                        {copiedId === 'generated' ? (
                          <Check className="mr-2 h-3 w-3" />
                        ) : (
                          <Copy className="mr-2 h-3 w-3" />
                        )}
                        Copy
                      </Button>
                      <Button variant="outline" size="sm">
                        <Download className="mr-2 h-3 w-3" />
                        Export BibTeX
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Recent Citations
              </CardTitle>
              <CardDescription>
                Your recently generated citations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {SAMPLE_CITATIONS.map((citation) => (
                  <Card key={citation.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="secondary">{citation.format}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(citation.createdAt)}
                            </span>
                          </div>
                          <h4 className="font-medium text-sm mb-2">{citation.resourceTitle}</h4>
                          <div className="p-3 bg-muted/50 rounded-lg">
                            <p className="text-sm font-mono text-muted-foreground whitespace-pre-wrap">
                              {citation.formattedCitation}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopy(citation.id, citation.formattedCitation)}
                          >
                            {copiedId === citation.id ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Impact Tab */}
        <TabsContent value="impact" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Citation Trends
                </CardTitle>
                <CardDescription>
                  How your research is being cited over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={citationTrendData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="month" className="text-xs" />
                      <YAxis className="text-xs" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="citations"
                        stroke="#667eea"
                        strokeWidth={2}
                        dot={{ fill: '#667eea' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Network className="h-5 w-5" />
                  Citation Network
                </CardTitle>
                <CardDescription>
                  Research connected through citations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">Incoming Citations</p>
                      <p className="text-sm text-muted-foreground">Works that cite your research</p>
                    </div>
                    <p className="text-3xl font-bold text-primary">1,847</p>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">Outgoing Citations</p>
                      <p className="text-sm text-muted-foreground">Works your research cites</p>
                    </div>
                    <p className="text-3xl font-bold text-primary">423</p>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">Co-Citation Score</p>
                      <p className="text-sm text-muted-foreground">Research cited together</p>
                    </div>
                    <p className="text-3xl font-bold text-primary">87</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Cited Resources */}
          <Card>
            <CardHeader>
              <CardTitle>Most Cited Resources</CardTitle>
              <CardDescription>Your highest impact research outputs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { title: 'Climate Change Impact on Great Barrier Reef...', citations: 156, trend: '+12' },
                  { title: 'Machine Learning Framework for Astronomical...', citations: 89, trend: '+8' },
                  { title: 'Indigenous Land Management Practices...', citations: 67, trend: '+5' },
                  { title: 'Antarctic Ice Core Data: 800,000 Year...', citations: 45, trend: '+3' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.title}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{item.citations}</p>
                      <p className="text-xs text-green-600">{item.trend} this month</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate Citation Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Citation</DialogTitle>
            <DialogDescription>
              Enter a DOI or paste resource details to generate a citation
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>DOI (Recommended)</Label>
              <Input placeholder="e.g., 10.1234/example.2024.001" />
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or enter manually</span>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input placeholder="Resource title" />
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Input placeholder="2024" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Authors</Label>
              <Input placeholder="Author names (comma separated)" />
            </div>

            <div className="space-y-2">
              <Label>Citation Format</Label>
              <Select value={selectedFormat} onValueChange={(v) => setSelectedFormat(v as Citation['format'])}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CITATION_FORMATS.map((format) => (
                    <SelectItem key={format.value} value={format.value}>
                      {format.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </Button>
            <Button>
              <BookOpen className="mr-2 h-4 w-4" />
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
